<template>
  <div id="programme-page">
    <div class="programme-banner">
      <k-banner v-if="programmeDetails" :cover-image="coverImage">
        <div class="heading-container content-with-spacing">
          <div class="title-container">
            <h1 v-if="programmeDetails">
              <i class="fas fa-chart-bar"></i> {{ programmeDetails.name }}
            </h1>
          </div>
          <div v-if="availableProgrammes.length > 1" class="header-dropdown">
            <span class="dropdown-label">Programmes:</span>
            <k-dropdown :options="availableProgrammes" v-model="currentProgrammeId"></k-dropdown>
          </div>
          <div v-if="programmeDetails" class="intro-text">
            <div v-if="programmeDetails.description" v-html="$kpurify.sanitise(programmeDetails.description)"></div>
            <p v-else><b class="welcome-text">Welcome to the {{programmeDetails.name}} programme!</b><br />
              Below is a list of modules for this programme. To get started on your first module, click on the "View Module" button.
            </p>
          </div>
        </div>
      </k-banner>
    </div>
    <div class="content-with-spacing" v-if="programmeInfoReady">
      <programme-content
        :programme-id="programmeDetails.id"
        @programmeContentReady="onProgrammeContentReady">
      </programme-content>
    </div>
    <k-chat-box v-if="$featureFlags.isEnabled('kate-bot', 'programme-page') && programmeInfoReady"
      :programmeId="programmeDetails.id">
    </k-chat-box>
  </div>
</template>

<style>
/* Dropdown */
.header-dropdown .k-search-dropdown-menu {
  min-width: 250px;
}

/* If navcrumb is empty */
.content-with-spacing .k-breadcrumb:empty {
  display: none;
}

</style>

<style scoped>
.heading-container {
  display: flexbox;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  padding: 40px 0;
  margin-bottom: 30px;
}

.heading-container .dropdown-label {
  margin-right: 0.5em;
}

.title-container {
  flex: 1 1 60%;
  align-self: center;
}

.title-container h1 {
  text-transform: capitalize;
  padding: 15px 0 !important;
}

.header-dropdown {
  display: flex;
  align-items: center;
}

.intro-text {
  width: 100%;
  margin-bottom: 15px;
}

.intro-text a i {
  padding-left: 10px;
}

@media (max-width: 1204px) {
  .header-dropdown {
    margin-bottom: 20px;
    align-items: center;
  }
}

@media (max-width: 767px) {
  .title-container h1 {
    margin: 0 !important;
  }

  .programme-banner > .heading-container {
    margin-top: 0 !important;
  }
}
</style>

<script>
import { storeToRefs } from 'pinia';
import useAppProgrammeStore from '@stores/app-programme-store';
import KDropdown from '@base-components/k-dropdown.vue';
import TimeMixin from '@mixins/time-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KChatBox from '@base-components/chatbot/k-chat-box.vue';
import ProgrammeContent from './programme-content.vue';
import KBanner from './k-banner.vue';

export default {
  components: {
    KDropdown,
    ProgrammeContent,
    KBanner,
    KChatBox,
  },

  mixins: [TimeMixin, PageReadyMixin],

  data() {
    const programmeStore = useAppProgrammeStore();
    const { currentProgrammeId } = storeToRefs(programmeStore);

    return {
      programmeStore,
      currentProgrammeId,
      programmeContentReady: false,
      isResolving: false,
    };
  },

  beforeMount() {
    this.$Loading.start();
  },

  created() {
    this.resolveProgrammeId();
  },

  watch: {
    currentProgrammeId(newVal) {
      if (!newVal) return;
      const currentRouteProgrammeId = Number(this.$route.params.programmeId);
      if (newVal !== currentRouteProgrammeId) {
        this.programmeContentReady = false;
      }
    },
    programmeDetails(newVal) {
      if (newVal) {
        this.registerCrumbs();
      }
    },
    programmeIdFromRoute(newVal) {
      if (!newVal) {
        // No programme ID in route, resolve it. Like going to programme page without programmeId
        this.resolveProgrammeId();
      }
    },
  },

  computed: {
    ready() {
      return this.programmeDetails && this.programmeContentReady;
    },
    availableProgrammes() {
      return this.programmeStore.programmes;
    },
    programmeIdFromRoute() {
      const isProgrammeRoute = this.$route.matched.some(record => record.path === '/programmes/:programmeId?/:track?');
      const paramId = Number(this.$route.params.programmeId);
      return isProgrammeRoute && !Number.isNaN(paramId) ? paramId : undefined;
    },
    programmeDetails() {
      return this.programmeStore.getProgramme(this.programmeStore.currentProgrammeId);
    },
    coverImage() {
      return this.programmeDetails?.cover_image;
    },
    programmeInfoReady() {
      return this.programmeDetails && this.programmeStore.currentProgrammeId;
    },
  },

  methods: {
    resolveProgrammeId() {
      if (this.isResolving) return;
      this.isResolving = true;
      this.programmeStore.currentProgrammeId = null;
      this.$logger.info('Resolving programme ID from route or store');
      const newProgrammeId = this.programmeIdFromRoute || this.programmeStore.defaultProgrammeId;
      if (newProgrammeId) {
        if (this.programmeStore.isProgrammeAccessible(newProgrammeId)) {
          this.$logger.info('Programme ID resolved: ', { newProgrammeId });
          this.programmeStore.currentProgrammeId = newProgrammeId;
        } else {
          this.$logger.info('User is not a member of the requested programme');
          this.$router.push({ name: '404' });
          this.programmeStore.currentProgrammeId = null;
        }
      } else {
        this.$logger.info('User is not a member of any programmes');
        this.$router.push('/user_not_in_programme');
        this.programmeStore.currentProgrammeId = null;
      }
      this.isResolving = false;
    },
    onProgrammeContentReady() {
      this.programmeContentReady = true;
      if (Number(this.$route.params.programmeId) !== this.programmeStore.currentProgrammeId) {
        this.$router.replace({
          name: 'programme_curriculum',
          params: { programmeId: this.programmeStore.currentProgrammeId },
        });
      }
    },
    registerCrumbs() {
      this.$crumbs.register([
        {
          text: this.programmeDetails.name,
          active: true,
        },
      ]);
    },
  },
};
</script>
